import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useMFA, TOTPEnrollData } from '@/hooks/useMFA';
import { useAuth } from '@/hooks/useAuth';
import { useTrustedDevice } from '@/hooks/useTrustedDevice';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Shield,
  ShieldCheck,
  Smartphone,
  Copy,
  Check,
  ArrowLeft,
  KeyRound,
  Loader2,
  LogOut,
  Monitor,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function MFAPage() {
  const [searchParams] = useSearchParams();
  const mode = (searchParams.get('mode') || 'verify') as 'enroll' | 'verify';
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { enrollTOTP, challengeAndVerify, listFactors, loading } = useMFA();
  const { setTrusted } = useTrustedDevice();
  const { toast } = useToast();

  // Enrollment state
  const [enrollData, setEnrollData] = useState<TOTPEnrollData | null>(null);
  const [enrollStep, setEnrollStep] = useState<'qr' | 'verify'>('qr');

  // Verification state
  const [verifyFactorId, setVerifyFactorId] = useState<string | null>(null);

  // Shared
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [trustedDays, setTrustedDays] = useState(60);
  const [secretCopied, setSecretCopied] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const from = (location.state as any)?.from || '/dashboard';

  // ── Initialisation ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) {
      navigate('/auth', { replace: true });
      return;
    }
    // Charger la durée configurée pour les appareils de confiance
    supabase.from('site_settings').select('value').eq('key', 'mfa_trusted_device_days').maybeSingle()
      .then(({ data }) => { if (data) setTrustedDays(Number((data as any).value ?? 60)); });
    if (mode === 'enroll') {
      startEnrollment();
    } else {
      loadVerifyFactor();
    }
  }, [user, mode]);

  useEffect(() => {
    // Auto-focus code input
    setTimeout(() => inputRef.current?.focus(), 300);
  }, [enrollStep, pageLoading]);

  const startEnrollment = async () => {
    setPageLoading(true);
    const { data, error } = await enrollTOTP();
    if (error) {
      // Si un facteur non-vérifié existe déjà, le supprimer et réessayer
      if (error.message?.includes('existing')) {
        const { data: factors } = await listFactors();
        const unverified = factors?.totp?.find((f) => f.status === 'unverified');
        if (unverified) {
          await supabase.auth.mfa.unenroll({ factorId: unverified.id });
          const retry = await enrollTOTP();
          if (retry.data) {
            setEnrollData(retry.data);
            setEnrollStep('qr');
          }
        }
      } else {
        toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
      }
    } else if (data) {
      setEnrollData(data);
      setEnrollStep('qr');
    }
    setPageLoading(false);
  };

  const loadVerifyFactor = async () => {
    setPageLoading(true);
    const { data } = await listFactors();
    const verified = data?.totp?.find((f) => f.status === 'verified');
    if (!verified) {
      // Pas de facteur vérifié → passer en mode enroll
      navigate('/mfa?mode=enroll', { state: location.state, replace: true });
      return;
    }
    setVerifyFactorId(verified.id);
    setPageLoading(false);
  };

  // ── Soumission du code ───────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.replace(/\s/g, '');
    if (trimmed.length !== 6) {
      toast({ title: 'Code invalide', description: 'Entrez les 6 chiffres affichés dans votre application.', variant: 'destructive' });
      return;
    }

    const factorId = mode === 'enroll' ? enrollData?.id : verifyFactorId;
    if (!factorId) return;

    const { error } = await challengeAndVerify(factorId, trimmed);
    if (error) {
      toast({ title: 'Code incorrect', description: 'Vérifiez le code dans votre application et réessayez.', variant: 'destructive' });
      setCode('');
      inputRef.current?.focus();
      return;
    }

    // Si l'utilisateur veut mémoriser l'appareil
    if (rememberDevice && user) {
      await setTrusted(user.id);
    }

    toast({
      title: mode === 'enroll' ? 'MFA activé !' : 'Vérification réussie',
      description: mode === 'enroll'
        ? `Votre compte est protégé par MFA.${rememberDevice ? ` Cet appareil est mémorisé pour ${trustedDays} jours.` : ''}`
        : `Connecté avec succès.${rememberDevice ? ` Cet appareil est mémorisé pour ${trustedDays} jours.` : ''}`,
    });
    navigate(from, { replace: true });
  };

  const copySecret = async () => {
    if (!enrollData?.totp?.secret) return;
    await navigator.clipboard.writeText(enrollData.totp.secret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth', { replace: true });
  };

  // ── Rendu ────────────────────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/20 p-4">
      <div className="w-full max-w-md space-y-4">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-2">
            {mode === 'enroll' ? (
              <Shield className="h-8 w-8 text-primary" />
            ) : (
              <ShieldCheck className="h-8 w-8 text-primary" />
            )}
          </div>
          <h1 className="text-2xl font-bold">
            {mode === 'enroll' ? 'Configurer le MFA' : 'Vérification en deux étapes'}
          </h1>
          <p className="text-muted-foreground text-sm">
            {mode === 'enroll'
              ? 'Protégez votre compte avec Google/Microsoft Authenticator'
              : 'Entrez le code affiché dans votre application d\'authentification'}
          </p>
        </div>

        {/* ── ENROLL MODE ── */}
        {mode === 'enroll' && enrollData && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {enrollStep === 'qr' ? 'Étape 1 — Scanner le QR code' : 'Étape 2 — Confirmer'}
                </CardTitle>
                <div className="flex gap-1">
                  <Badge variant={enrollStep === 'qr' ? 'default' : 'outline'}>1</Badge>
                  <Badge variant={enrollStep === 'verify' ? 'default' : 'outline'}>2</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {enrollStep === 'qr' ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    Ouvrez <strong>Google Authenticator</strong>, <strong>Microsoft Authenticator</strong>{' '}
                    ou <strong>Authy</strong>, puis scannez ce QR code :
                  </p>

                  {/* QR Code SVG */}
                  <div className="flex justify-center p-4 bg-white rounded-xl border">
                    <div
                      className="w-48 h-48"
                      dangerouslySetInnerHTML={{ __html: enrollData.totp.qr_code }}
                    />
                  </div>

                  {/* Secret manuel */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1.5">
                      Ou entrez manuellement la clé secrète :
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-muted rounded px-3 py-2 text-xs font-mono tracking-widest break-all">
                        {enrollData.totp.secret}
                      </code>
                      <Button size="icon" variant="outline" onClick={copySecret}>
                        {secretCopied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <Button className="w-full" onClick={() => setEnrollStep('verify')}>
                    <Smartphone className="h-4 w-4 mr-2" />
                    J'ai scanné le QR code
                  </Button>
                </>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Entrez le code à 6 chiffres affiché dans votre application pour confirmer la configuration.
                  </p>

                  <div className="space-y-2">
                    <Label htmlFor="totp-code">Code de vérification</Label>
                    <Input
                      id="totp-code"
                      ref={inputRef}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      className="text-center text-2xl tracking-[0.5em] font-mono"
                    />
                  </div>

                  {/* Remember device */}
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Checkbox
                      id="remember-enroll"
                      checked={rememberDevice}
                      onCheckedChange={(v) => setRememberDevice(Boolean(v))}
                    />
                    <label htmlFor="remember-enroll" className="text-sm cursor-pointer flex items-center gap-2">
                      <Monitor className="h-4 w-4 text-muted-foreground" />
                      Se souvenir de cet appareil pendant <strong>{trustedDays} jours</strong>
                    </label>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
                    Activer le MFA
                  </Button>

                  <Button type="button" variant="ghost" size="sm" className="w-full" onClick={() => setEnrollStep('qr')}>
                    <ArrowLeft className="h-4 w-4 mr-1" /> Retour au QR code
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── VERIFY MODE ── */}
        {mode === 'verify' && (
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg text-sm">
                  <Smartphone className="h-5 w-5 text-primary flex-shrink-0" />
                  <span>Ouvrez votre application d'authentification et entrez le code affiché pour <strong>Nazandoo</strong>.</span>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="verify-code">Code à 6 chiffres</Label>
                  <Input
                    id="verify-code"
                    ref={inputRef}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="text-center text-2xl tracking-[0.5em] font-mono"
                  />
                </div>

                {/* Remember device */}
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <Checkbox
                    id="remember-verify"
                    checked={rememberDevice}
                    onCheckedChange={(v) => setRememberDevice(Boolean(v))}
                  />
                  <label htmlFor="remember-verify" className="text-sm cursor-pointer flex items-center gap-2">
                    <Monitor className="h-4 w-4 text-muted-foreground" />
                    Se souvenir de cet appareil pendant <strong>{trustedDays} jours</strong>
                  </label>
                </div>

                <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                  Vérifier
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Sign out link */}
        <div className="text-center">
          <Button variant="ghost" size="sm" className="text-muted-foreground gap-2" onClick={handleSignOut}>
            <LogOut className="h-4 w-4" />
            Se déconnecter
          </Button>
        </div>
      </div>
    </div>
  );
}
